// Golden Wilds (Cole, 2026-10-27): now and then, while the game is on screen (or the corner window is open), a golden
// copy of the hunt's wild shows on the field for about ten seconds. A click pays about two minutes of the trainer's
// own hunt pace in coins and XP; the team's creatures get their share of the XP too. Nothing else: no catch, no drop.
//
// The server decides when one shows (server/world.ts goldenTick), checks the click and caps them per hour. Time
// counts only while the hunt runs with the game open and seen: never away hunting, a fast-forward or playtest speed,
// and never during the tutorial's hold or the name, look and starter steps.
import type { AwayPace } from './offline.ts';

export const GOLDEN = {
  /** Hunting on screen between two Golden Wilds: a gap between these two, different each time. */
  minGapMs: 10 * 60_000,
  maxGapMs: 20 * 60_000,
  /** At most this many show in any hour, per trainer. */
  perHour: 4,
  /** How long one stays on the field. */
  windowMs: 10_000,
  /** A click can reach the server this much after the window closed (the connection's delay). */
  graceMs: 2_000,
  /** The reward: this much of the trainer's hunt pace (shared/offline.ts HuntMeter), in coins and XP. */
  paceMs: 2 * 60_000,
  /** The least one pays, for a trainer whose pace is still thin. */
  floorCoins: 25,
  floorXp: 25,
} as const;

const HOUR_MS = 3_600_000;

/** A Golden Wild as the client gets it. left: the ms it still has on the field; ms: its whole time there; x and y: where it stands on the scene (0 to 1). */
export interface GoldenDTO { id: number; species: string; left: number; ms: number; x: number; y: number }

/** What a click pays: coins, trainer XP, and XP for each team creature as [id, xp]. */
export interface GoldenReward { coins: number; xp: number; cx: [number, number][] }

/** A number from 0 to 1 out of a seed, so a Golden Wild never draws on the hunt's own random numbers (the sims replay them). */
export function goldenHash(seed: number): number {
  let h = (Math.floor(seed) ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 2 ** 32;
}

/** The hunting on screen before the next Golden Wild. */
export function goldenGap(seed: number): number {
  return GOLDEN.minGapMs + Math.floor(goldenHash(seed) * (GOLDEN.maxGapMs - GOLDEN.minGapMs));
}

/** Where one stands on the scene: clear of the edges, the hunt card on top and the scene's tools on the right. */
export function goldenSpot(id: number): { x: number; y: number } {
  const r = (n: number) => Math.round(n * 1000) / 1000;
  return { x: r(0.28 + goldenHash(id) * 0.4), y: r(0.4 + goldenHash(id + 7) * 0.3) };
}

/** Spawn times still inside the hour before `now`. */
export function goldenRecent(at: readonly number[] | undefined, now: number): number[] {
  return (at ?? []).filter((t) => now - t < HOUR_MS && t <= now);
}

/** Whether another may show now: fewer than GOLDEN.perHour in the last hour. */
export function goldenRoom(at: readonly number[] | undefined, now: number): boolean {
  return goldenRecent(at, now).length < GOLDEN.perHour;
}

/**
 * What a click pays: GOLDEN.paceMs of the hunt pace `p`, at least the floor. Creature XP goes only to the creatures
 * on `team` now, each in the share it earned on the hunt.
 */
export function goldenReward(p: AwayPace | undefined, team: readonly number[]): GoldenReward {
  const k = p && p.ms > 0 ? GOLDEN.paceMs / p.ms : 0;
  const on = new Set(team);
  const cx = (p?.cx ?? []).filter(([id]) => on.has(id)).map(([id, n]) => [id, Math.floor(n * k)] as [number, number]).filter(([, n]) => n > 0);
  return {
    coins: Math.max(GOLDEN.floorCoins, Math.floor((p?.coins ?? 0) * k)),
    xp: Math.max(GOLDEN.floorXp, Math.floor((p?.xp ?? 0) * k)),
    cx,
  };
}

/** Catch sound choices (Settings.catchSound): off, grade A and up, or every catch kept. */
export const CATCH_SOUNDS = ['off', 'A', 'all'] as const;
export type CatchSound = (typeof CATCH_SOUNDS)[number];
export const isCatchSound = (v: unknown): v is CatchSound => (CATCH_SOUNDS as readonly unknown[]).includes(v);
